/* eslint-disable no-console */
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
} as const

type LogLevel = keyof typeof LOG_LEVELS

// Get log level from environment or default based on build
const DEFAULT_LOG_LEVEL: LogLevel = import.meta.env.PROD ? "info" : "debug"
const CURRENT_LOG_LEVEL = (import.meta.env.FRONTEND_LOG_LEVEL || DEFAULT_LOG_LEVEL) as LogLevel

class Logger {
  private static instance: Logger = new Logger()
  private logLevel: number

  private constructor() {
    this.logLevel = LOG_LEVELS[CURRENT_LOG_LEVEL]
  }

  public static getInstance(): Logger {
    return Logger.instance
  }

  private formatMessage(level: string, message: string, context?: object): string {
    const timestamp = new Date().toISOString()
    const contextString = context ? `\nContext: ${JSON.stringify(context, null, 2)}` : ""
    return `[${timestamp}] ${level.toUpperCase()}: ${message}${contextString}`
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= this.logLevel
  }

  public debug(message: string, context?: object): void {
    if (this.shouldLog("debug")) {
      console.debug(this.formatMessage("debug", message, context))
    }
  }

  public info(message: string, context?: object): void {
    if (this.shouldLog("info")) {
      console.info(this.formatMessage("info", message, context))
    }
  }

  public warn(message: string, context?: object): void {
    if (this.shouldLog("warn")) {
      console.warn(this.formatMessage("warn", message, context))
    }
  }

  public error(message: string, context?: object | Error): void {
    if (this.shouldLog("error")) {
      const errorContext =
        context instanceof Error
          ? {
              error: {
                message: context.message,
                stack: context.stack,
                name: context.name,
              },
            }
          : context

      console.error(this.formatMessage("error", message, errorContext))
    }
  }
}

export const logger = Logger.getInstance()
